'use client';

// Page d'erreur affichee quand une route App Router crash cote client.
// Multilingue (fr, en, es, de, it, pt, nl, pl, ar) avec detection navigator.language.
// Pointe d'humour facon Vyndra : on dedramatise sans etre clownesque.
// Plus jamais de "Application error: a client-side exception has occurred" devant nos users.

import { motion } from 'framer-motion';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { ArrowsClockwise as RefreshCw, House as Home, CaretDown as ChevronDown, WarningCircle as AlertCircle } from '@phosphor-icons/react';
import { VyndraLogo } from '@/components/vyndra-logo';
import { Button } from '@/components/ui/button';
import { FondEcosysteme } from '@/components/fonds/fond-ecosysteme';

type Lang = 'fr' | 'en' | 'es' | 'de' | 'it' | 'pt' | 'nl' | 'pl' | 'ar';

const translations: Record<Lang, {
  title: string;
  subtitle: string;
  description: string;
  reload: string;
  home: string;
  techDetails: string;
  errorCode: string;
  rtl?: boolean;
}> = {
  fr: {
    title: 'Une virgule a fait des siennes',
    subtitle: "Quelque chose s'est cassé. C'est nous, pas vous.",
    description: "Notre équipe est notifiée automatiquement. Réessayez dans un instant, ça repart neuf fois sur dix.",
    reload: 'Recharger',
    home: "Retour à l'accueil",
    techDetails: 'Détails techniques',
    errorCode: 'Code',
  },
  en: {
    title: 'A semicolon went rogue',
    subtitle: 'Something broke. On our side, not yours.',
    description: 'Our team is notified automatically. Give it a moment and try again, it usually fixes itself.',
    reload: 'Reload',
    home: 'Back to home',
    techDetails: 'Technical details',
    errorCode: 'Code',
  },
  es: {
    title: 'Una coma se rebeló',
    subtitle: 'Algo se rompió. Es cosa nuestra, no tuya.',
    description: 'Nuestro equipo ha sido notificado automáticamente. Inténtalo de nuevo en un momento, suele funcionar a la segunda.',
    reload: 'Recargar',
    home: 'Volver al inicio',
    techDetails: 'Detalles técnicos',
    errorCode: 'Código',
  },
  de: {
    title: 'Ein Semikolon ist außer Kontrolle',
    subtitle: 'Etwas ist schiefgelaufen. Bei uns, nicht bei Ihnen.',
    description: 'Unser Team wird automatisch benachrichtigt. Versuchen Sie es gleich noch einmal, meistens klappt es dann.',
    reload: 'Neu laden',
    home: 'Zur Startseite',
    techDetails: 'Technische Details',
    errorCode: 'Code',
  },
  it: {
    title: 'Una virgola si è ribellata',
    subtitle: 'Qualcosa si è rotto. Colpa nostra, non tua.',
    description: 'Il nostro team è stato avvisato automaticamente. Riprova tra un istante, di solito si risolve da solo.',
    reload: 'Ricarica',
    home: 'Torna alla home',
    techDetails: 'Dettagli tecnici',
    errorCode: 'Codice',
  },
  pt: {
    title: 'Uma vírgula saiu do controlo',
    subtitle: 'Algo correu mal. Connosco, não consigo.',
    description: 'A nossa equipa foi notificada automaticamente. Tente novamente daqui a um momento, geralmente resolve-se sozinho.',
    reload: 'Recarregar',
    home: 'Voltar ao início',
    techDetails: 'Detalhes técnicos',
    errorCode: 'Código',
  },
  nl: {
    title: 'Een puntkomma is op hol geslagen',
    subtitle: 'Er ging iets mis. Bij ons, niet bij u.',
    description: 'Ons team is automatisch op de hoogte. Probeer het zo opnieuw, meestal lost het zichzelf op.',
    reload: 'Opnieuw laden',
    home: 'Terug naar start',
    techDetails: 'Technische details',
    errorCode: 'Code',
  },
  pl: {
    title: 'Średnik wymknął się spod kontroli',
    subtitle: 'Coś się zepsuło. Po naszej stronie, nie Twojej.',
    description: 'Nasz zespół został automatycznie powiadomiony. Spróbuj ponownie za chwilę, zwykle naprawia się samo.',
    reload: 'Odśwież',
    home: 'Wróć do strony głównej',
    techDetails: 'Szczegóły techniczne',
    errorCode: 'Kod',
  },
  ar: {
    title: 'فاصلة منقوطة خرجت عن السيطرة',
    subtitle: 'حدث خطأ ما. من جهتنا، لا من جهتكم.',
    description: 'تم إخطار فريقنا تلقائياً. حاول مرة أخرى بعد لحظات, عادةً ما يتم إصلاحه ذاتياً.',
    reload: 'إعادة التحميل',
    home: 'العودة إلى الرئيسية',
    techDetails: 'التفاصيل التقنية',
    errorCode: 'الرمز',
    rtl: true,
  },
};

function detectLang(): Lang {
  if (typeof navigator === 'undefined') return 'fr';
  const lang = (navigator.language || 'fr').toLowerCase().split('-')[0];
  if (lang in translations) return lang as Lang;
  return 'fr';
}

export default function ErrorPage({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [lang, setLang] = useState<Lang>('fr');
  const [showDetails, setShowDetails] = useState(false);
  const t = translations[lang];

  useEffect(() => {
    setLang(detectLang());

    // ChunkLoadError : après un déploiement, la PWA/onglet en cache référence des
    // chunks JS qui n'existent plus -> "Loading chunk X failed". On recharge UNE
    // fois automatiquement (garde anti-boucle 15 s) pour récupérer le build neuf.
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      /loading chunk|loading css chunk|dynamically imported module|failed to fetch dynamically/i.test(error?.message ?? '');
    if (isChunkError && typeof window !== 'undefined') {
      try {
        const KEY = 'vyndra-chunk-reload-at';
        const last = Number(sessionStorage.getItem(KEY) || '0');
        if (Date.now() - last > 15000) {
          sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
          return;
        }
      } catch { /* sessionStorage indispo : on laisse l'écran d'erreur */ }
    }

    // Notification du backend (best-effort, non-bloquant)
    // En cas d'echec on reste silencieux : pas la peine de cascader une erreur sur une erreur.
    fetch('/api/errors/report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: error.message,
        stack: error.stack?.slice(0, 4000),
        digest: error.digest,
        url: typeof window !== 'undefined' ? window.location.href : null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      }),
    }).catch(() => {});
  }, [error]);

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden flex items-center justify-center p-6"
      dir={t.rtl ? 'rtl' : 'ltr'}
    >
      {/* Le fond commun, celui de la connexion (29/09/2026). Il remplace deux halos
          flous animes sans fin et une grille. */}
      <FondEcosysteme selonHote />

      <div className="relative z-10 flex flex-col items-center text-center max-w-[560px]">
        {/* Logo + icone alerte cote a cote */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mb-6 relative"
        >
          <VyndraLogo size={72} />
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.4, type: 'spring', damping: 14, stiffness: 220 }}
            className="absolute -top-2 -right-2 w-[28px] h-[28px] rounded-full bg-[#EF4444] flex items-center justify-center shadow-[0_0_0_3px_#08090B,0_4px_12px_rgba(239,68,68,0.4)]"
          >
            <AlertCircle size={14} className="text-white" weight="bold" />
          </motion.div>
        </motion.div>

        {/* Big title with gradient */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="font-display text-[42px] font-bold leading-[1.1] tracking-[-1px] mb-4 max-w-[480px]"
          style={{
            background: 'linear-gradient(135deg, #EF4444 0%, #8B5CF6 50%, #22D3EE 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {t.title}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <p className="text-[14px] text-[#E8E9EB] mb-3 font-medium">
            {t.subtitle}
          </p>
          <p className="text-[12.5px] text-[#8A8F98] leading-relaxed max-w-[460px]">
            {t.description}
          </p>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.5 }}
          className="flex gap-3 mt-8"
        >
          <Button
            variant="secondary"
            size="lg"
            onClick={() => reset()}
            className="group"
          >
            <RefreshCw
              size={14}
              className="transition-transform group-hover:rotate-180"
              style={{ transitionDuration: '500ms' }}
            />
            {t.reload}
          </Button>
          <Link href="/dashboard">
            <Button variant="primary" size="lg" className="group">
              <Home size={14} />
              {t.home}
              <span className={t.rtl ? 'mr-1 transition-transform group-hover:-translate-x-0.5' : 'ml-1 transition-transform group-hover:translate-x-0.5'}>
                {t.rtl ? '←' : '→'}
              </span>
            </Button>
          </Link>
        </motion.div>

        {/* Details techniques (replies par defaut) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-10 w-full max-w-[460px]"
        >
          <button
            type="button"
            onClick={() => setShowDetails((v) => !v)}
            className="flex items-center gap-1.5 mx-auto text-[10.5px] text-[#5A5F6A] hover:text-[#8A8F98] transition-colors font-mono"
          >
            <ChevronDown
              size={11}
              className="transition-transform"
              style={{ transform: showDetails ? 'rotate(0deg)' : 'rotate(-90deg)' }}
            />
            {t.techDetails}
          </button>
          {showDetails && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              transition={{ duration: 0.2 }}
              className="mt-3 overflow-hidden"
            >
              <div className="rounded-lg border border-[#1E2028] bg-[#0F1012]/60 backdrop-blur-md p-4 text-left">
                <div className="text-[10px] text-[#5A5F6A] font-mono mb-1">
                  {t.errorCode}
                </div>
                <code className="text-[10.5px] text-[#FCA5A5] font-mono break-all">
                  {error.digest ?? error.name ?? 'UNKNOWN'}
                </code>
                {error.message && (
                  <>
                    <div className="text-[10px] text-[#5A5F6A] font-mono mt-3 mb-1">
                      Message
                    </div>
                    <code className="text-[10.5px] text-[#C4C7CC] font-mono break-words">
                      {error.message}
                    </code>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
